import React, { useMemo } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Select from "@mui/material/Select";
import { useMyGameEditions } from "@seabrookstudios/pitch2table";
import { formatFullDate } from "../../core/date-formatting";
import { DateTime } from "luxon";

const Label = "Edition";

/**
 * @param {import("@seabrookstudios/pitch2table-core").GameEdition} a
 * @param {import("@seabrookstudios/pitch2table-core").GameEdition} b
 */
const sortEditions = (a, b) => {
  if (!a.datePublished) {
    return -1;
  }
  if (!b.datePublished) {
    return 1;
  }

  const aDate = DateTime.fromMillis(a.datePublished).startOf("day");
  const bDate = DateTime.fromMillis(b.datePublished).startOf("day");

  if (aDate.equals(bDate)) {
    return a.name.localeCompare(b.name);
  }

  return b.datePublished - a.datePublished;
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").GameEditionId|"unset"} props.value
 * @param {(value: import("@seabrookstudios/pitch2table-core").GameEditionId|"unset") => void} props.onChange
 * @param {React.ReactNode} props.helperText
 * @param {boolean} [props.disabled]
 */
export const EditionSelect = ({ gameId, value, onChange, helperText, disabled = false }) => {
  const { data: editions = [], isLoading } = useMyGameEditions(gameId);

  /**
   * @param {*} ev
   */
  const onChangeHandler = (ev) => {
    onChange(ev.target.value);
  };

  const sortedEditions = useMemo(() => {
    return editions.sort(sortEditions);
  }, [editions]);

  return (
    <FormControl sx={{ width: "100%" }} margin="normal" disabled={disabled}>
      <InputLabel id="edition">{Label}</InputLabel>
      <Select
        labelId="edition"
        value={value}
        onChange={onChangeHandler}
        label={Label}
        input={<OutlinedInput label={Label} />}
      >
        <MenuItem key="none" value="unset">
          None
        </MenuItem>
        {isLoading ? (
          <CircularProgress />
        ) : (
          sortedEditions.map((edition) => {
            const secondary = edition.datePublished ? ` • ${formatFullDate(edition.datePublished)}` : "";
            return (
              <MenuItem key={edition.id} value={edition.id}>
                {edition.name}
                {secondary}
              </MenuItem>
            );
          })
        )}
      </Select>
      <FormHelperText>{helperText}</FormHelperText>
    </FormControl>
  );
};
